import React, { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { ZONES } from '../config/zones';
import { useStore } from '../store';

// A small gold glowing Info Pin at each zone. Click/tap it to slide in that
// zone's briefing side-card. No floating text — the 3D view stays clear.
function Pin({ zone }) {
  const dot = useRef();
  const halo = useRef();
  const beam = useRef();
  const [hover, setHover] = useState(false);
  const setSideCard = useStore((s) => s.setSideCard);
  const activeSideCard = useStore((s) => s.activeSideCard);
  const open = activeSideCard === zone.index;

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.2 + zone.index);
    if (dot.current) dot.current.position.y = 6 + Math.sin(t * 1.6 + zone.index) * 0.6;
    if (halo.current) {
      halo.current.rotation.z = t * 0.8;
      const s = (open ? 1.5 : hover ? 1.3 : 1) * (1 + pulse * 0.12);
      halo.current.scale.setScalar(s);
      halo.current.material.opacity = (open ? 0.9 : 0.45) + pulse * 0.2;
    }
    if (beam.current) beam.current.material.opacity = (open ? 0.4 : 0.12) + pulse * 0.06;
  });

  const onClick = (e) => { e.stopPropagation(); setSideCard(open ? null : zone.index); };

  return (
    <group position={[zone.x, 0, 0]}>
      {/* thin locator beam */}
      <mesh ref={beam} position={[0, 22, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 44, 6]} />
        <meshBasicMaterial color="#D4AF37" transparent opacity={0.14} depthWrite={false} />
      </mesh>
      {/* clickable glowing pin */}
      <group
        onClick={onClick}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <mesh ref={dot} position={[0, 6, 0]}>
          <sphereGeometry args={[hover || open ? 1.5 : 1.1, 16, 16]} />
          <meshBasicMaterial color="#ffe58a" toneMapped={false} />
        </mesh>
        {/* Hover label — tiny zone name so desktop users know what they'll open */}
        {hover && !open && (
          <Html position={[0, 10, 0]} center distanceFactor={60} zIndexRange={[40, 0]} style={{ pointerEvents: 'none' }}>
            <div style={{ whiteSpace: 'nowrap', padding: '4px 10px', borderRadius: 9999, background: 'rgba(6,9,15,0.85)', border: '1px solid rgba(212,175,55,0.5)', color: '#ffe58a', fontFamily: 'monospace', fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {String(zone.number).padStart(2, '0')} · {zone.title}
            </div>
          </Html>
        )}
        {/* halo ring standing upright at the dot */}
        <mesh ref={halo} position={[0, 6, 0]}>
          <ringGeometry args={[1.7, 2.1, 32]} />
          <meshBasicMaterial color="#D4AF37" transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
        {/* larger invisible hit area for easy tapping */}
        <mesh position={[0, 6, 0]} visible={false}>
          <sphereGeometry args={[5, 8, 8]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
      </group>
      {/* ground ring marker */}
      <mesh position={[0, 0.4, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[4, 4.6, 32]} />
        <meshBasicMaterial color="#D4AF37" transparent opacity={0.3} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function ZoneMarkers() {
  const panelsHidden = useStore((s) => s.panelsHidden);
  if (panelsHidden) return null;
  return (
    <group>
      {ZONES.map((z) => (
        <Pin key={z.id} zone={z} />
      ))}
    </group>
  );
}
